/**
 * Per-persona landing-page content — the source data for /for/<slug>
 * pages rendered by PersonaLearnPage.tsx.
 *
 * Different shape than signalLearnContent.ts because the audience-first
 * page answers a different question. /learn pages answer "what is X
 * signal"; /for pages answer "I'm a Y, what does Avium do for me."
 *
 * Structure:
 *   1. Hero — "Avium for <role>" + tagline that lands the value prop
 *      in one sentence.
 *   2. Day-in-the-life problem — the recurring pain this role lives.
 *   3. What changes with Avium — the practical workflow shift.
 *   4. Featured Signals — 3-5 Avium Signals this persona uses most,
 *      cross-linked to /learn/<slug> for SEO + content depth.
 *   5. CTA card.
 *   6. Related personas cross-link (one team's SM is another team's EM).
 *
 * SEO target (per the 2026-05-26 user direction): high-intent searches
 * like "scrum master reporting tools," "agile coach dashboard," "VP
 * engineering metrics," "engineering manager 1:1 prep" — searches
 * where the searcher has a job title AND a buying motion.
 */

import type { MarketingVisualKind } from '../components/marketing/marketingVisualData';

export interface PersonaSection {
  heading: string;
  body: string;
}

export interface PersonaFeaturedSignal {
  /** Signal slug (must exist in signalLearnContent.ts when shipped) */
  slug: string;
  /** Why this Signal matters specifically to this persona. */
  why: string;
}

export interface PersonaLearnContent {
  slug: string;
  role: string;
  pageTitle: string;
  metaDescription: string;
  tagline: string;
  /** Branded product visual shown under the hero — the picture that sells this
   *  role on Avium at a glance (see components/marketing). */
  heroVisual?: MarketingVisualKind;
  /** A second visual shown deeper in the page (after "what changes"), with an
   *  optional caption. Used to surface Avium Intelligence — the AI briefing
   *  differentiator — to decision-maker personas (VP, PMO) who buy on it. */
  bodyVisual?: MarketingVisualKind;
  bodyVisualCaption?: string;

  dayInTheLife: PersonaSection;
  whatChanges: PersonaSection & { workflowShifts: string[] };
  featuredSignals: PersonaFeaturedSignal[];
  cta: { heading: string; body: string };
  relatedSlugs: string[];
}

export const PERSONA_LEARN_CONTENT: Record<string, PersonaLearnContent> = {
  'scrum-masters': {
    slug: 'scrum-masters',
    role: 'Scrum Masters',
    pageTitle: 'Avium for scrum masters — daily briefing, sprint risk, and standup running order',
    metaDescription: "Avium gives scrum masters a daily briefing computed from your Jira data — or any work management tool — sprint risk, blocked work, over-allocation, and scope change. Walk into standup with the agenda already written.",
    tagline: "Walk into standup knowing what to discuss. Avium reads your team's Jira data — or any work management tool — overnight and hands you the agenda: sprint risk, blockers, over-allocation, the things that actually matter today.",
    heroVisual: 'intelligence-briefing',

    dayInTheLife: {
      heading: "Your morning, today",
      body: "It's 8:45 AM. Standup is at 9:00. You open Jira, scroll the active sprint, scan for tickets that look stuck. You open three columns and try to remember what was 'in review' yesterday versus this morning. You DM Sarah to ask about APP-43 — wait, isn't that the same ticket from Tuesday? You check the burndown chart. It looks fine. You walk into standup, and within four minutes someone says something that surprises you, and you realize Sarah's been blocked since Monday and nobody flagged it.\n\nThe scrum master's job is to surface what the team can't see. The tools mostly hand you the same data the team already has and ask you to read it faster. That's the gap Avium closes.",
    },

    whatChanges: {
      heading: "What changes with Avium",
      body: "Avium reads your Jira data on a schedule and computes the Avium Signals overnight. By 6:30 AM the briefing is ready. You open it on your phone or laptop and the standup agenda is already written:",
      workflowShifts: [
        "Standup running order — Avium ranks active blockers by age. The oldest one is the first thing you discuss; the team learns this pattern in a sprint or two.",
        "Sprint risk score — current sprint's likelihood of hitting commit. If it's down, the briefing names the three drivers (e.g., 'two in-review tickets stale 6 days; Mike at 130% capacity').",
        "Over-allocation list — the people whose committed work exceeds capacity. You raise this before sprint planning, not during retro.",
        "Mid-sprint scope changes — tickets added or removed since the sprint started. The receipt you'll need at retro when leadership asks why this sprint slipped.",
        "Data-quality sentinel — how trustworthy your forecast is. Bad data = unreliable signal; the briefing tells you which tickets to clean up.",
      ],
    },

    featuredSignals: [
      { slug: 'sprint-risk-forecast', why: 'The headline metric. One number you can quote at standup; one set of drivers you can act on.' },
      { slug: 'over-allocation', why: "Catches your team's burnout risk before the engineer brings it up themselves." },
      { slug: 'velocity-drift', why: 'Honest retro material. Bring data instead of vibes when the team is talking about why this sprint felt heavier.' },
      { slug: 'sprint-scope-change', why: "Documents what got added mid-sprint. Critical evidence when leadership asks why commitments slipped." },
    ],

    cta: {
      heading: 'See your team in Avium',
      body: "Free tier connects to your work management tool (Jira today, more integrations on the way), pulls your active sprint, and computes the Signals in under a minute. The full Avium Intelligence briefing — the plain-English narrative on top — is on the Business tier. Most scrum masters start free and upgrade after a sprint or two.",
    },

    relatedSlugs: ['agile-coaches', 'engineering-managers'],
  },

  'agile-coaches': {
    slug: 'agile-coaches',
    role: 'Agile Coaches',
    pageTitle: 'Avium for agile coaches — cross-team pattern detection and retro evidence',
    metaDescription: "Avium gives agile coaches the data layer for cross-team coaching — velocity drift, hand-off cost, ping-pong patterns, and workflow process mining. Bring evidence to the retro.",
    tagline: "Coaching a team without data is theater. Avium reads the patterns in your Jira flow — or any work management tool — and gives you the evidence — process anti-patterns, retro action item progress, velocity dynamics — across every team you coach.",
    heroVisual: 'workflow-flow',

    dayInTheLife: {
      heading: "The coach's invisible problem",
      body: "You coach four teams. Each one has its own scrum master, its own velocity, its own dysfunction. You sit in their retros and you can tell something's off — Team A keeps committing more than they ship; Team B has tickets ping-ponging between people; Team C says they're blocked but their burndown looks normal.\n\nThe evidence is in Jira. You don't have the time to dig through four Jira instances every week. You ask the SMs to surface it; they show you a spreadsheet they made on Sunday night. By the time the data reaches you, it's stale, and you're coaching from anecdotes.",
    },

    whatChanges: {
      heading: 'What changes with Avium',
      body: "Avium runs the same pattern detection across every team in the organization. You filter by team in the picker, see that team's specific signals; you remove the filter, see the org pattern.",
      workflowShifts: [
        'Hand-off cost — tickets that change assignees more than twice. Predicts reopen rate + cycle time; the signal of a team that hasn\'t internalized ownership.',
        'Ping-pong detection — tickets bouncing between two phases or two people. The hidden anti-pattern that retros never name because each individual move feels reasonable.',
        'Workflow Sankey — process mining over the real status transitions in your data. Reveals where tickets actually flow versus where you THINK they flow.',
        'Reopen rate — what percent of done work comes back. Quality signal; trending up means definition-of-done has eroded.',
        'Time-in-status — per-phase dwell time. Where work piles up. Often answers a question nobody knew to ask.',
      ],
    },

    featuredSignals: [
      { slug: 'hand-off-cost', why: 'The single best leading indicator of cycle-time problems. Every coach should know this number per team.' },
      { slug: 'workflow-sankey', why: 'The map of how work actually flows. Hidden bottlenecks become visible patterns.' },
      { slug: 'ping-pong-detection', why: 'The retro topic nobody brings up because each individual instance feels reasonable.' },
      { slug: 'velocity-drift', why: 'Compare drift across the teams you coach. A cliff in one team is local; cliffs in three teams is structural.' },
    ],

    cta: {
      heading: 'Coach with data, not stories',
      body: "Avium connects to your org's work management tool (Jira today, more integrations on the way) and surfaces the same signals across every team. The picker lets you scope the view to a single team or compare across all of them. Free tier covers up to 3 projects; coaches typically run on Team or Business.",
    },

    relatedSlugs: ['scrum-masters', 'engineering-managers'],
  },

  'engineering-managers': {
    slug: 'engineering-managers',
    role: 'Engineering Managers',
    pageTitle: 'Avium for engineering managers — capacity planning, 1:1 prep, and sprint forecasting',
    metaDescription: "Avium gives engineering managers a capacity-aware view of every sprint, over-allocation alerts, and 1:1-ready data on each engineer. Stop guessing at workload; plan with evidence.",
    tagline: "The two questions every engineering manager keeps answering: can we hit this sprint, and is my team stretched too thin. Avium answers both with the data already in Jira — or any work management tool.",
    heroVisual: 'over-allocation',
    bodyVisual: 'intelligence-briefing',
    bodyVisualCaption: "Avium Intelligence reads every Signal on your team — sprint risk, over-allocation, blockers — ranks them, and hands you the plain-language briefing of what's at risk and what to do first. The AI layer that turns the data into your standup and 1:1 agenda before problems become slips.",

    dayInTheLife: {
      heading: 'The manager\'s two recurring decisions',
      body: "Decision one: 'can we take this on?' You're in a planning meeting. Product wants three more tickets in the sprint. Your gut says no but you don't have a number to back it up. So you commit, hedge, and pay for it at sprint review.\n\nDecision two: 'who's overloaded?' You do 1:1s every other week. You want to ask each engineer how their load is. They say 'fine' because that's what people say. Two weeks later one of them is short-tempered, one is taking sick days, and your skip-level wants to know what's happening.",
    },

    whatChanges: {
      heading: 'What changes with Avium',
      body: "Avium gives you a capacity model that's accurate AT the moment of the decision. Sprint Forecast Matrix shows the next sprint already loaded with currently-committed work, per person, with capacity headroom visible. Over-Allocation runs continuously; you don't have to ask.",
      workflowShifts: [
        "Sprint Forecast Matrix — who can take what next sprint, before planning starts. Push back on scope with a specific number instead of a feeling.",
        "Capacity Tracker — current sprint's load by person + by team, including mid-sprint adjustments (PTO entered today, headcount changes).",
        "Over-allocation Signal — the people above 105% capacity, ranked. The list you bring to 1:1 prep.",
        "Assign Work — drag-and-drop tickets to people; conflicts surface as you create them. Avium tells you in the planning meeting, not the retro.",
        "Cross-team load — if you manage multiple teams or report up to a VP, the per-team rollup shows where capacity is unevenly distributed.",
      ],
    },

    featuredSignals: [
      { slug: 'over-allocation', why: 'The single most actionable signal for managers. Tells you who needs a 1:1 conversation today.' },
      { slug: 'sprint-risk-forecast', why: 'Quantifies "will we hit this sprint" so you can answer your VP without hedging.' },
      { slug: 'velocity-drift', why: 'Plan honestly — committing the same load to a volatile team as a stable team is how teams burn out.' },
      { slug: 'data-quality-score', why: "If your forecast is reading from bad data, you're managing with a broken instrument. Avium tells you when." },
    ],

    cta: {
      heading: 'See your team\'s capacity in Avium',
      body: "Free tier shows you the over-allocation Signal on your real Jira data. Capacity Tracker and Sprint Forecast Matrix unlock on the Team plan; the AI Briefing layer that summarizes the daily state lives on Business. Most managers start free and upgrade once their team has used the workload board for a sprint.",
    },

    relatedSlugs: ['scrum-masters', 'vps'],
  },

  'vps': {
    slug: 'vps',
    role: 'VPs of Engineering',
    pageTitle: 'Avium for VPs of engineering — cross-team rollup, release health, and board-ready metrics',
    metaDescription: "Avium gives VPs of engineering a cross-team rollup of sprint risk, capacity, and release health. The board update that takes 3 minutes instead of 3 hours.",
    tagline: "The data exists. Your managers each have their own dashboard. None of them roll up to the number you need for the board. Avium fixes that.",
    heroVisual: 'sprint-risk',
    bodyVisual: 'intelligence-briefing',
    bodyVisualCaption: "Avium Intelligence is the differentiator: it reads every Signal across your teams, ranks what's at risk, and writes the plain-language briefing — so you act on the short list that reduces risk, not ten dashboards you have to interpret.",

    dayInTheLife: {
      heading: "The board-update problem",
      body: "Your CEO asks: are we going to ship by quarter-end? You don't know. You go to your three engineering managers; they each have their own story. One has a polished dashboard you trust. One has a spreadsheet. One says 'it's fine.' You aggregate the stories in your head, decide you trust the first two managers more than the third, and tell the CEO 'we're on track but tight.' The third manager's team misses.\n\nThe issue isn't lack of data. It's that nothing aggregates the data the way you actually need it: by team, rolled up to org, with the risk flagged.",
    },

    whatChanges: {
      heading: 'What changes with Avium',
      body: "One organization. One model. Every team's data computed on the same engine, surfaced through the same set of Signals, rolled up to the org level.",
      workflowShifts: [
        "Release health — every active FixVersion with % done, days to target, at-risk pill. The view your board update needs.",
        "Sprint risk rollup — current sprint risk across every team, ranked. Drill into the red ones; the green ones don't need your attention this week.",
        "Capacity-by-team — utilization bars per team or department. Spot the squeeze and the slack at the same time.",
        "Velocity trend — multi-sprint trends across the org, cliffs flagged. Honest planning conversation, not vibes.",
        "Data quality — what percent of your forecast is reading from clean data. The number that tells you how much to trust the rest.",
      ],
    },

    featuredSignals: [
      { slug: 'sprint-risk-forecast', why: 'The headline metric across every active sprint, rolled up. One screen for "what\'s at risk this week."' },
      { slug: 'velocity-drift', why: 'Multi-team drift detection. Three teams cliffing in the same quarter is an org-level pattern worth investigating.' },
      { slug: 'over-allocation', why: 'Department-level burnout signal before it shows up in attrition. Hiring-plan input.' },
      { slug: 'data-quality-score', why: "Your forecast is only as good as your data. This number tells you how much faith to put in the rest." },
    ],

    cta: {
      heading: 'See your org in Avium',
      body: "Avium starts working from the first import — the engine computes Signals immediately, including the cross-team rollup. Free tier shows you Signals on demo data; connecting your work management tool (Jira today, more integrations on the way) pulls everything in. Org-level features and the AI Briefing layer are on Business and Enterprise.",
    },

    relatedSlugs: ['engineering-managers', 'pmos'],
  },

  'project-managers': {
    slug: 'project-managers',
    role: 'Project Managers',
    pageTitle: "Avium for project managers — your project's risk, blockers, and what slipped, every morning",
    metaDescription: "Avium gives project managers a live read on their project — what's at risk, who's blocked, and what changed since yesterday — built from your Jira data or any work management tool. Coordinate delivery instead of assembling status by hand.",
    tagline: "You're the one keeping delivery moving — chasing blockers, updating the plan, fielding 'are we on track?' Avium reads your project's work management data — Jira today, or any tool — and hands you the short list each morning: what's at risk, what's blocked, and what changed since yesterday.",
    heroVisual: 'sprint-risk',
    bodyVisual: 'workflow-flow',
    bodyVisualCaption: "Avium maps how your project's work actually flows and where it sticks — the blocked hand-offs and rework loops you'd otherwise find out about in the standup, surfaced before it.",

    dayInTheLife: {
      heading: "The 9am scramble",
      body: "It's 9am. You open the board, scan for anything red, and start the rounds — ping the dev who owns the gateway ticket, chase the design hand-off that was 'almost done' on Monday, check whether the dependency another team owed you actually landed. By the time standup starts you've half-assembled a picture in your head, and the one blocker that matters is the one nobody flagged.\n\nA PM's job is to keep delivery moving — to see the risk early and clear the path. The tools give you a board of tickets and leave the seeing to you, every single morning. That's the gap Avium closes.",
    },

    whatChanges: {
      heading: "What changes with Avium",
      body: "Avium reads your project on a schedule and hands you the morning read — so you start the day with the short list, not the scavenger hunt:",
      workflowShifts: [
        "Risk, named — your project's delivery risk scored and explained (behind pace, over capacity, blocked), so 'are we on track?' has an answer with a reason.",
        "Blockers surfaced — the blocked and stale work ranked oldest-first, before it ages quietly into a slip.",
        "What changed since yesterday — scope added or moved mid-sprint, with the timestamp — so a date moving never surprises you.",
        "Capacity you can see — who on the project is committed beyond capacity, before it turns into a missed hand-off.",
        "Less assembling, more coordinating — the status read is built for you; your time goes to clearing the path, not compiling the picture.",
      ],
    },

    featuredSignals: [
      { slug: 'sprint-risk-forecast', why: 'The headline. One score for "will this land," with the drivers you can actually act on.' },
      { slug: 'blocked-and-stale', why: 'Your morning rounds, pre-done — the blocked work ranked oldest-first so you clear the path before standup.' },
      { slug: 'sprint-scope-change', why: 'What got added or moved mid-sprint, with the receipt — so a slipping date never blindsides you.' },
      { slug: 'over-allocation', why: 'Who on the project is underwater — the missed hand-off you can prevent with a 1:1.' },
    ],

    cta: {
      heading: 'See your project clearly in Avium',
      body: "Free tier connects your work management tool (Jira today, more integrations on the way) and computes the Signals for your project in minutes. The full Avium Intelligence briefing — the plain-language morning read on top — is on the Business tier. Most PMs start free and upgrade once the morning scramble turns into a 30-second scan.",
    },

    relatedSlugs: ['scrum-masters', 'pmos'],
  },

  'pmos': {
    slug: 'pmos',
    role: 'PMOs',
    pageTitle: 'Avium for PMOs — portfolio status, cross-project risk, and the report that writes itself',
    metaDescription: "Avium gives PMOs a live rollup of every project — status, cross-project risk, capacity, and the slip you can flag before the steering committee. Built from your Jira data, or any work management tool.",
    tagline: "The status report shouldn't eat your whole Thursday. Avium reads every project's work management data — Jira today, or any tool — and hands you the portfolio rollup: what's on track, what's at risk, and the number you can take to the steering committee.",
    heroVisual: 'sprint-risk',
    bodyVisual: 'intelligence-briefing',
    bodyVisualCaption: "Avium Intelligence reads every Signal across your projects, ranks what's at risk, and drafts the plain-language status narrative — the AI layer that turns a portfolio of data into the short list you act on to keep delivery on track.",

    dayInTheLife: {
      heading: "Status-report Thursday",
      body: "It's Thursday. The steering committee is Friday at 10. You open six projects, ping four leads for an update, and start copy-pasting into the deck. Two reply by end of day; the others you guess at. You color the RAG statuses green, amber, amber, green — best judgment — and by the time you present, one of the greens is already a slip nobody escalated. The committee asks why, and you don't have the receipt for what changed.\n\nThe PMO's job is to give leadership a picture of delivery they can trust. The tools hand you raw tickets, one project at a time, and ask you to assemble that picture by hand — every single week. That's the gap Avium closes.",
    },

    whatChanges: {
      heading: "What changes with Avium",
      body: "Avium reads every connected project on a schedule and assembles the portfolio view continuously. The status report becomes something you review, not something you build:",
      workflowShifts: [
        "Portfolio rollup — every project's health in one view (on track / at risk / slipping), with the driver named. No hand-maintained RAG spreadsheet that's stale by Friday.",
        "Cross-project risk — sprint risk scored the same way across every team, ranked. 'Which projects are in trouble' is a sorted list, not a gut call.",
        "Capacity across projects — the person committed beyond capacity across several projects at once — invisible on each individual board, obvious on the aggregate.",
        "Scope & schedule change — what got added or moved mid-cycle, with the timestamped receipt for the committee when a date moves.",
        "The report that writes itself — Avium Intelligence drafts the plain-language status narrative; you edit and send, you don't assemble.",
      ],
    },

    featuredSignals: [
      { slug: 'sprint-risk-forecast', why: 'The portfolio headline. One risk score per project, ranked — the steering-committee slide assembles itself.' },
      { slug: 'over-allocation', why: 'Catches the cross-project over-commit no single board shows — the person underwater across three projects at once.' },
      { slug: 'sprint-scope-change', why: "The receipt. When a date slips, this documents what was added or moved mid-cycle — evidence for the committee, not blame." },
      { slug: 'blocked-and-stale', why: 'The blockers aging quietly across every project, ranked oldest-first — escalate before they reach your status report.' },
    ],

    cta: {
      heading: 'See your whole portfolio in Avium',
      body: "Free tier connects your work management tool (Jira today, more integrations on the way) and computes the Signals across your projects in minutes. The full Avium Intelligence rollup — the plain-language status narrative on top — is on the Business tier. Most PMOs start free and upgrade once leadership sees the first status view that assembled itself.",
    },

    relatedSlugs: ['vps', 'engineering-managers'],
  },
};

export function getPersonaContent(slug: string | undefined): PersonaLearnContent | null {
  if (!slug) return null;
  return PERSONA_LEARN_CONTENT[slug] ?? null;
}
